import React, { FC, ReactNode, useEffect, useRef } from 'react';

interface IComponentProps {
  children: React.ReactNode;
  loading?: boolean;
  hasMore?: boolean;
  loader?: ReactNode;
  scrollToTopTrigger?: any[];
  loadMore?: () => void | Promise<void>;
  onScroll?: (value: string) => void;
}

export const InfiniteScroll: FC<IComponentProps> = (props) => {
  const { children, loading, hasMore, loader, scrollToTopTrigger = [], loadMore, onScroll } = props;

  const scrollRef = useRef<HTMLDivElement>(null);

  const lastInputDirection = useRef<'up' | 'down' | null>(null);
  const lastTouchY = useRef(0);
  const inputTimeout = useRef<any>(null);
  // Защита от шторма дублей: scroll-event сыплется десятками в секунду, а hasMore флипается
  // только после резолва async loadMore — без guard'а уходит куча одинаковых запросов.
  const inFlightRef = useRef(false);

  const setDirection = (dir: 'up' | 'down') => {
    lastInputDirection.current = dir;

    clearTimeout(inputTimeout.current);
    inputTimeout.current = setTimeout(() => {
      lastInputDirection.current = null;
    }, 120);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY > 0) {
        setDirection('down');
      } else if (e.deltaY < 0) {
        setDirection('up');
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: true });

    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const handleTouchStart = (e: TouchEvent) => {
      lastTouchY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;

      if (currentY < lastTouchY.current) {
        setDirection('down');
      } else if (currentY > lastTouchY.current) {
        setDirection('up');
      }

      lastTouchY.current = currentY;
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;

      if (lastInputDirection.current && onScroll) {
        onScroll(lastInputDirection.current);
      }

      if (loadMore && hasMore && !inFlightRef.current && scrollHeight - scrollTop <= clientHeight + 100) {
        inFlightRef.current = true;
        Promise.resolve(loadMore()).finally(() => {
          inFlightRef.current = false;
        });
      }
    };

    el.addEventListener('scroll', handleScroll);

    return () => el.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadMore, onScroll]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    lastInputDirection.current = null;
  }, [...scrollToTopTrigger]);

  return (
    <div
      ref={scrollRef}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'auto',
        scrollbarWidth: 'none',
      }}
    >
      {children}

      {loading && loader && (
        <div
          style={{
            position: 'relative',
            minHeight: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {loader}
        </div>
      )}
    </div>
  );
};
